import { Component, OnInit } from '@angular/core';
import { Observable, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil } from 'rxjs/operators';

import { MobileContextService } from '@core/context/services/mobile-context.service';
import { AbstractContextualList } from '@shared/base/abstract-contextual-list.directive';
import { DateUtils } from '@shared/date-utils';
import { ContextoPWA } from '@shared/types';

import {
  PlanejamentoAula,
  PlanejamentoFiltros,
  PlanejamentoSituacaoAprovacao,
  PLANEJAMENTO_SITUACAO_LABELS,
  PeriodoAvaliativoPlanejamento,
  mapApiItemToPlanejamento
} from '../models/planejamento-aula.types';

import { PeriodoSelectOption } from '@infrastructure/ui/components/forms/periodo-select/periodo-select.component';
import { PlanejamentoAulaService } from '../services/planejamento-aula.service';

const TIPOS_ATIVIDADE = ['ATIVIDADE_COMPLEMENTAR', 'ATENDIMENTO_EDUCACIONAL_ESPECIALIZADO'] as const;

interface EstruturaPeriodos {
  periodos:             PeriodoAvaliativoPlanejamento[];
  encerramentos:        any[];
  semCalendario:        boolean;
  semPeriodoAvaliativo: boolean;
}

@Component({
  selector: 'app-planejamento-aula',
  templateUrl: './planejamento-aula.component.html',
  styleUrls: ['./planejamento-aula.component.scss']
})
export class PlanejamentoAulaComponent extends AbstractContextualList<PlanejamentoAula> implements OnInit {

  private disciplinasMap = new Map<number, string>();

  // ─── Empty states ─────────────────────────────────────────────────────────
  semCalendario = false;
  semPeriodoAvaliativo = false;

  // ─── Modal de detalhe ────────────────────────────────────────────────────
  modalDetalheAberta = false;
  registroVisualizado: PlanejamentoAula | null = null;

  // ─── Filtros ──────────────────────────────────────────────────────────────
  termoFiltro = '';
  filtroData = '';
  filtroDataSelecionada: Date | null = null;
  filtroSituacao: PlanejamentoSituacaoAprovacao | null = null;

  // ─── Período Avaliativo ───────────────────────────────────────────────────
  periodos: PeriodoAvaliativoPlanejamento[] = [];
  periodoSelecionado: PeriodoAvaliativoPlanejamento | null = null;
  private encerramentosTurma: any[] = [];
  private idsEncerrados = new Set<number>();
  private periodosPronto = false;

  readonly opcoesSituacao: Array<{ valor: PlanejamentoSituacaoAprovacao; label: string }> = [
    { valor: 'EM_ELABORACAO',        label: PLANEJAMENTO_SITUACAO_LABELS.EM_ELABORACAO },
    { valor: 'AGUARDANDO_APROVACAO', label: PLANEJAMENTO_SITUACAO_LABELS.AGUARDANDO_APROVACAO },
    { valor: 'AGUARDANDO_CORRECAO',  label: PLANEJAMENTO_SITUACAO_LABELS.AGUARDANDO_CORRECAO },
    { valor: 'EM_ANALISE',           label: PLANEJAMENTO_SITUACAO_LABELS.EM_ANALISE },
    { valor: 'APROVADO',             label: PLANEJAMENTO_SITUACAO_LABELS.APROVADO }
  ];

  // ─── Getters ──────────────────────────────────────────────────────────────

  get turmaNome(): string {
    return this.contexto?.turma?.descricao ?? this.contexto?.turma?.nome ?? '';
  }

  /** Mapeia períodos para o seletor; usa Set para verificar encerramento sem iterar a lista. */
  get periodoOptions(): PeriodoSelectOption[] {
    return this.periodos.map((p) => ({
      id:          p.id,
      descricao:   p.descricao,
      dataInicial: p.dataInicial,
      dataFinal:   p.dataFinal,
      encerrado:   this.idsEncerrados.has(p.id)
    }));
  }

  get periodoSelecionadoId(): number | string | null {
    return this.periodoSelecionado?.id ?? null;
  }

  // ─────────────────────────────────────────────────────────────────────────

  constructor(
    private planejamentoService: PlanejamentoAulaService,
    mobileContextService: MobileContextService
  ) {
    super(mobileContextService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  protected override aoContextoMudar(_contexto: ContextoPWA | null): void {
    this.disciplinasMap       = new Map();
    this.periodos             = [];
    this.periodoSelecionado   = null;
    this.encerramentosTurma   = [];
    this.idsEncerrados        = new Set();
    this.periodosPronto       = false;
    this.semCalendario        = false;
    this.semPeriodoAvaliativo = false;
  }

  protected override carregarDadosApi(isLoadMore: boolean, contexto: ContextoPWA): void {
    if (!this.periodosPronto && !isLoadMore) {
      this.carregarPeriodosEListar(contexto);
      return;
    }
    this.carregarListagem(isLoadMore, contexto);
  }

  // ─── Período ─────────────────────────────────────────────────────────────

  onPeriodoOptionSelected(option: PeriodoSelectOption): void {
    const encontrado = this.periodos.find((p) => p.id === option.id);
    this.periodoSelecionado = encontrado ?? {
      id:          Number(option.id),
      descricao:   option.descricao,
      dataInicial: option.dataInicial ?? '',
      dataFinal:   option.dataFinal   ?? ''
    };
    this.recarregar();
  }

  // ─── Filtros gerais ───────────────────────────────────────────────────────

  onFiltroChange(): void {
    this.recarregar();
  }

  onFiltroDataChange(data: Date | null): void {
    this.filtroDataSelecionada = data;
    this.filtroData = data ? DateUtils.formatarDataISO(data) : '';
    this.recarregar();
  }

  onSituacaoChange(valor: string | null): void {
    this.filtroSituacao = valor as PlanejamentoSituacaoAprovacao | null;
    this.recarregar();
  }

  // ─── Modal de detalhe ────────────────────────────────────────────────────

  visualizarRegistro(registro: PlanejamentoAula): void {
    this.registroVisualizado     = registro;
    this.modalDetalheAberta      = true;
    document.body.style.overflow = 'hidden';
  }

  fecharModalDetalhe(): void {
    this.modalDetalheAberta      = false;
    this.registroVisualizado     = null;
    document.body.style.overflow = '';
  }

  private carregarPeriodosEListar(contexto: ContextoPWA): void {
    this.carregando           = true;
    this.semCalendario        = false;
    this.semPeriodoAvaliativo = false;

    this.obterEstruturaPeriodos$(contexto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (estrutura) => {
          this.encerramentosTurma   = estrutura.encerramentos;
          this.periodos             = estrutura.periodos;
          this.semCalendario        = estrutura.semCalendario;
          this.semPeriodoAvaliativo = estrutura.semPeriodoAvaliativo;
          this.reconstruirSetEncerrados();

          if (!this.semCalendario && !this.semPeriodoAvaliativo && !this.periodoSelecionado) {
            this.periodoSelecionado = this.selectPeriodoAutomatico(this.periodos);
          }

          this.periodosPronto = true;
          this.carregarListagem(false, contexto);
        }
      });
  }

  private obterEstruturaPeriodos$(contexto: ContextoPWA): Observable<EstruturaPeriodos> {
    const turmaId      = contexto.turma!.id;
    const idComponente = (contexto.componentes ?? [])[0]?.id;

    if (this.isAtividadeOrModular() && idComponente) {
      return this.planejamentoService.getItemEducacional(idComponente).pipe(
        catchError(() => of(null)),
        map((item) => this.criarEstruturaDeItemEducacional(item))
      );
    }

    return forkJoin({
      periodos:      this.planejamentoService.getPeriodosAvaliativos(turmaId),
      encerramentos: this.planejamentoService.getEncerramentosTurma(turmaId)
    }).pipe(
      catchError(() => of({ periodos: [] as any[], encerramentos: [] as any[] })),
      switchMap(({ periodos, encerramentos }) => {
        const mappedPeriodos = this.mapearPeriodos(periodos);

        if (mappedPeriodos.length === 0 && this.isAtividadeOrModular() && idComponente) {
          return this.planejamentoService.getItemEducacional(idComponente).pipe(
            catchError(() => of(null)),
            map((item) => this.criarEstruturaDeItemEducacional(item))
          );
        }

        return of<EstruturaPeriodos>({
          periodos:             mappedPeriodos,
          encerramentos,
          semCalendario:        mappedPeriodos.length === 0,
          semPeriodoAvaliativo: false
        });
      })
    );
  }

  private mapearPeriodos(raw: any[]): PeriodoAvaliativoPlanejamento[] {
    return raw.map((p) => ({
      id:          p.id,
      descricao:   p.descricao   ?? 'Período',
      dataInicial: p.dataInicial ?? '',
      dataFinal:   p.dataFinal   ?? ''
    }));
  }

  private criarEstruturaDeItemEducacional(item: any): EstruturaPeriodos {
    if (item?.dataInicio && item?.dataFim) {
      return {
        periodos: [{
          id:          0,
          descricao:   'Período da atividade',
          dataInicial: item.dataInicio,
          dataFinal:   item.dataFim
        }],
        encerramentos:        [],
        semCalendario:        false,
        semPeriodoAvaliativo: false
      };
    }

    return {
      periodos:             [],
      encerramentos:        [],
      semCalendario:        false,
      semPeriodoAvaliativo: true
    };
  }

  private reconstruirSetEncerrados(): void {
    this.idsEncerrados = new Set<number>(
      this.encerramentosTurma
        .filter((enc: any) => enc.status === 'ENCERRADO')
        .map((enc: any) =>
          typeof enc.periodoAvaliativo === 'object'
            ? enc.periodoAvaliativo?.id
            : (enc.periodoAvaliativo ?? enc.periodoId)
        )
        .filter((id: any) => id != null)
    );
  }

  private carregarListagem(isLoadMore: boolean, contexto: ContextoPWA): void {
    const turmaId       = contexto.turma!.id;
    const filtros       = this.montarFiltros();
    const disciplinaCtx = this.resolverDisciplinaContexto(contexto);

    if (isLoadMore) {
      this.carregandoMais = true;
    } else {
      this.carregando = true;
    }

    combineLatest([
      this.resolverItens$(turmaId),
      this.planejamentoService.getRegistrosRaw(contexto, filtros, this.offset, this.limit)
    ])
      .pipe(
        map(([mapaDisciplinas, registrosRaw]) =>
          registrosRaw.map((p) => {
            const vm = mapApiItemToPlanejamento(p, mapaDisciplinas);
            vm.disciplinaExibicao = this.extrairDisciplinas(p, mapaDisciplinas) || disciplinaCtx;
            return vm;
          })
        ),
        takeUntil(this.destroy$),
        finalize(() => {
          this.carregando     = false;
          this.carregandoMais = false;
        })
      )
      .subscribe({
        next: (novosRegistros) => {
          this.temMaisResultados = novosRegistros.length === this.limit;
          this.registros = isLoadMore
            ? [...this.registros, ...novosRegistros]
            : novosRegistros;
        },
        error: () => {
          if (!isLoadMore) { this.registros = []; }
          this.temMaisResultados = false;
        }
      });
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  private montarFiltros(): PlanejamentoFiltros {
    return {
      termo:             this.termoFiltro.trim() || undefined,
      data:              this.filtroData         || undefined,
      situacao:          this.filtroSituacao     ?? undefined,
      dataInicioPeriodo: this.periodoSelecionado?.dataInicial ?? undefined,
      dataFimPeriodo:    this.periodoSelecionado?.dataFinal   ?? undefined
    };
  }

  private selectPeriodoAutomatico(
    periodos: PeriodoAvaliativoPlanejamento[]
  ): PeriodoAvaliativoPlanejamento | null {
    if (!periodos.length) { return null; }

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const ativo = periodos.find((p) => {
      if (!p.dataInicial || !p.dataFinal) { return false; }
      const ini = new Date(p.dataInicial);
      const fim = new Date(p.dataFinal);
      ini.setHours(0, 0, 0, 0);
      fim.setHours(23, 59, 59, 999);
      return hoje >= ini && hoje <= fim;
    });

    if (ativo) { return ativo; }

    return [...periodos].sort((a, b) => {
      const da = a.dataFinal ? new Date(a.dataFinal).getTime() : 0;
      const db = b.dataFinal ? new Date(b.dataFinal).getTime() : 0;
      return db - da;
    })[0] ?? null;
  }

  private resolverDisciplinaContexto(contexto: ContextoPWA): string {
    const componentes = contexto.componentes ?? [];
    return componentes.length === 1
      ? (componentes[0].descricao || componentes[0].nome || '')
      : componentes.map(c => c.descricao || c.nome || '').filter(Boolean).join(', ');
  }

  private extrairDisciplinas(registroRaw: any, mapa: Map<number, string>): string {
    const itensDiretos: any[]   = registroRaw?.itensEducacionais ?? [];
    const itensAninhados: any[] = (registroRaw?.itensConteudos ?? [])
      .flatMap((ic: any) => ic?.itensEducacionais ?? []);

    const itens = itensDiretos.length ? itensDiretos : itensAninhados;

    const nomes = itens.map((ie: any) => {
      const id = ie?.itemEducacional?.id;
      return (id !== undefined && mapa.get(id))
        || ie?.itemEducacional?.descricao
        || ie?.itemEducacional?.nome;
    }).filter(Boolean);

    return [...new Set(nomes)]
      .sort((a: string, b: string) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }))
      .join(', ');
  }

  private resolverItens$(turmaId: number): Observable<Map<number, string>> {
    if (this.disciplinasMap.size > 0) { return of(this.disciplinasMap); }
    return this.planejamentoService.getItensEducacionaisRaw(turmaId).pipe(
      map((itens) => {
        const mapa = new Map<number, string>();
        itens.forEach((i) => mapa.set(i.id, i.descricao || i.nome || ''));
        this.disciplinasMap = mapa;
        return mapa;
      })
    );
  }

  private isAtividadeOrModular(): boolean {
    const turma = this.contexto?.turma;
    return TIPOS_ATIVIDADE.includes(turma?.tipoAtendimento as any)
      || turma?.etapaMatriz?.modular === true;
  }
}
